import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import {
  FormContainer,
  useForm,
  TextFieldElement,
  FieldValues,
} from "react-hook-form-mui";
import AddressInfo from "./AddressInfo";
import useRegisterN01User from "src/welcomeNonGemi/data-access/useRegisterQuery";
import ReCAPTCHA from "react-google-recaptcha";
import pako from "pako";
import { LegalPersonRegistrationValidationSchema } from "./validationSchema";
import { LoadingButton } from "@mui/lab";
import { checkAFM } from "src/applications/shared/lib/utils";

const LegalPersonRegstrationForm = () => {
  const { t } = useTranslation();
  const { isLoading: isLoadingRegister, mutate } = useRegisterN01User();
  const formContext = useForm({
    resolver: yupResolver(LegalPersonRegistrationValidationSchema()),
  });

  const handleRecaptcha = async (value: any) => {
    const compressedString = pako.gzip(value);
    const base64String = btoa(
      String.fromCharCode.apply(null, [...new Uint8Array(compressedString)]),
    );
    formContext.setValue("recaptcha", base64String);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
        maxWidth: "80vw",
        mx: "auto",
      }}
    >
      <Box>
        <Typography variant="h6" fontWeight="500">
          {t("Στοιχεία Νομικού Προσώπου (με αστερίσκο τα υποχρεωτικά πεδία)")}
        </Typography>
      </Box>
      <FormContainer
        formContext={formContext}
        onSuccess={async (data: FieldValues) => {
          if (data.n01_afm && !checkAFM(data.n01_afm)) {
            enqueueSnackbar(t("Μη έγκυρο ΑΦΜ"), {
              variant: "error",
            });
            return;
          }
          const formData = new FormData();

          Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append("regType", "legalPerson");
          mutate(formData);
        }}
        onError={(formError) => {
          Object.entries(formError).forEach(([key, value]) => {
            value?.message
              ? enqueueSnackbar(value?.message, {
                  variant: "error",
                })
              : null;
          });
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Grid
            container
            spacing={2}
            lg={9}
            rowSpacing={4}
            sx={{
              justifyContent: { xs: "center", md: "flex-start" },
            }}
          >
            <Grid item xs="auto">
              <TextFieldElement
                label={t("Επωνυμία Επιχείρησης")}
                name="n01_lastname"
                required
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement label={t("Email")} name="n01_email" required />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement
                label={t("Επιθυμητό Username")}
                name="n01_username"
                required
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement
                label={t("Password")}
                name="n01_password1"
                required
                type="password"
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement
                label={t("Password Επιβεβαίωση")}
                required
                name="n01_password2"
                type="password"
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement label={t("ΑΦΜ")} name="n01_afm" />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement
                label={t("Τηλέφωνο")}
                name="n01_phone"
                required
              />
            </Grid>
            <Grid item xs="auto">
              <TextFieldElement label={t("Κινητό")} name="n01_mobilephone" />
            </Grid>
          </Grid>
        </Box>
        <AddressInfo />
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
            gap: 2,
          }}
        >
          <ReCAPTCHA
            onChange={handleRecaptcha}
            sitekey={import.meta.env.VITE_REACT_APP_SITE_KEY}
          />

          <LoadingButton
            variant="contained"
            type="submit"
            loading={isLoadingRegister}
            disabled={!formContext.watch("recaptcha")}
          >
            {t("Εγγραφή")}
          </LoadingButton>
        </Box>
      </FormContainer>
    </Box>
  );
};

export default LegalPersonRegstrationForm;
